import { Fab } from "@rmwc/fab";
import { TextField } from "@rmwc/textfield";
import { HmacSHA256 } from "crypto-js";
import Base64 from 'crypto-js/enc-base64';
import { t } from "i18next";
import i18n from "i18next";
import { getNormalizedLanguageCode } from "utils/languageUtils";
import moment from "moment";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { ConfirmModal } from "Shared/ConfirmModal";

import { RESSOURCE_PRE_ACHATS } from "Achat/API/AchatApi";
import { selectTokenValue, selectUserEmail } from "App/Authentification/Store/selectors";
import { URL_BASE } from "App/Constants";
import { showError } from "App/Toast/Toast";
import { useSelector } from "react-redux";
import { getResource } from "Request/Components/RequestUtils";
import "./PaymentForm.css";

// #region CONSTANTES

const VALID_STATUS = ["AUTHORISED", "CAPTURED"];

const REFUSED_STATUSES = ["REFUSED", "CANCELLED", "ABORTED", "EXPIRED"];
const POLL_DELAY = 3000;
const POLL_MAX = 4;
const FALLBACK_POLL_DELAY_MS = 30000;
const FALLBACK_POLL_MAX = 2;
const FALLBACK_POLL_INTERVAL_MS = 2000;
const PENDING_ACHAT_KEY = "pendingAchat";
const PENDING_STAGE_KEY = "pendingAchatStage";
const STAGE_SUBMITTED = "SUBMITTED";
const STAGE_RETURNED = "RETURNED";

const AUTHORISED = "AUTHORISED"
const TEST = "TEST"
const PROD = "PRODUCTION"
const emailRegex = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/;
// AYB 2026.02.09 - Validation format email pour savoir si email user connu
const isValidEmailFormat = (emailStr) => {
  if (!emailStr || typeof emailStr !== 'string') return false;
  const trimmed = emailStr.trim();
  if (trimmed !== emailStr || /\s/.test(emailStr) || emailStr.length > 254) return false;
  if (!emailRegex.test(emailStr) || /\.{2,}/.test(emailStr) || emailStr.startsWith('.') || emailStr.startsWith('@') || emailStr.endsWith('.')) return false;
  return true;
};
// #endregion

// #region FONCTION COMPOSANT
/**
 * Composant permettant d'afficher le formulaire de paiement
 * @category PaymentForm
 */
function PaymentForm(props) {
  // #region INITIALISATION
  // Récupération des props
  const {
    borne,
    delaiPayzen,
    devise,
    montant,
    onBeforeSubmit,
    onErrorPaid,
    preAchatResult,
    onSuccessPaid
  } = props;

  // Initialisation des sélecteurs
  const userEmail = useSelector((state) => selectUserEmail(state));
  const tokenValue = useSelector((state) => selectTokenValue(state));

  // Initialisation du state
  // AYB 2026.02.09 - si email user connu et valide, considérer mail déjà validé (pas de saisie ni modale)
  const [mailInfo, setMailInfo] = useState(userEmail); // Mail de l'acheteur en cours
  const [mailValidate, setMailValidate] = useState(() => !!(userEmail && isValidEmailFormat(userEmail))); // True si le mail a été validé
  const [showErrorMessage, setShowErrorMessage] = useState(false); // Affiche un message d'erreur si l'adresse mail est incorrecte
  const [showMessageErrorPaiement, setShowMessageErrorPaiement] = useState(false);
  const [errorPaymentStatus, setErrorPaymentStatus] = useState(null); // REFUSED | CANCELLED | ABORTED | EXPIRED
  const [modaleConfirmMail, setModaleConfirmMail] = useState(false);
  const [signatureReady, setSignatureReady] = useState(false);

  // Initialisation des références
  const hasAutoSubmitted = useRef(false); // AYB 2026.02.09 - envoi direct formulaire vads quand email user connu
  const signature = useRef("");
  const dateMoment = useRef(moment().format("YYYYMMDDHHmmss"));
  //#endregion

  //#region UTILS
  // Renvoie la devise en string
  const getDevise = (value) => {
    switch (value) {
      // EUR //
      // Payzen
      case "978":
        return "EUR"

      // Urbaflux
      case "EUR":
        return "978"

      default:
        break;
    }
  };

  // Récupération des informations de Payzen
  const getPayzenInfos = (data) => {
    let obj = {}
    var dataToSplit = data;
    var qArray = dataToSplit.split('&');

    // Ajout des champs dans un objet
    for (var i = 0; i < qArray.length; i++) {
      let pArr = qArray[i].split('=');
      const key = pArr[0];
      const rawValue = pArr[1] ?? "";
      //AYB 2025.11.12 Décodage complet pour convertir les valeurs encodées par Payzen (ex: %40 -> @)
      // Remplace les + par des espaces puis décode les caractères encodés (ex: %40)
      const decodedValue = decodeURIComponent(rawValue.replace(/\+/g, " "));
      obj[key] = decodedValue;
    }

    let payzenInfos = {
      adresseMail: obj.vads_cust_email ?? "",
      autorisationPayzen: obj.vads_auth_number,
      numeroCarte: obj.vads_card_number,
      devise: getDevise(obj.vads_currency),
      montant: parseFloat((obj.vads_effective_amount / 100).toFixed(2)),
      modePaiement: obj.vads_ctx_mode,
      operationType: obj.vads_operation_type,
      transactionIdPayzen: obj.vads_trans_uuid,
      statusPayzen: obj.vads_auth_result,
      shopId: obj.vads_site_id,
    };

    if (obj.vads_trans_status === AUTHORISED) {
      onSuccessPaid(payzenInfos);
    } else {
      setShowMessageErrorPaiement(true);
      onErrorPaid(payzenInfos);
    }
  }

  // Langue iframe Payzen : fr, en, es, de, it, nl (normalisée depuis i18n)
  const payzenLanguage = getNormalizedLanguageCode(i18n.language);

  let PARAMETRES = {
    action_mode: "IFRAME",
    amount: montant.toString(),
    captureDelay: delaiPayzen,
    ctxMode: borne.zones.filter(zone => zone.idZone === 1)[0].paiementProd === 0 ? TEST : PROD,
    currency: getDevise(devise),
    email: mailInfo.trim(),
    language: payzenLanguage,
    order_id: "",
    info: borne.name,
    pageAction: "PAYMENT",
    paymentCard: "CB;VISA;MASTERCARD",
    paymentConfig: "SINGLE",
    redirectErrorTimeout: 0,
    redirectSuccessTimeout: 0,
    returnMode: "GET",
    siteId: borne.zones[0].BnqSiteId,
    themeConfig: "3DS_LOGOS=false",
    transDate: dateMoment.current,
    // transId: dateMoment.current.slice(8),
    transId: dateMoment.current.slice(8),
    urlReturn: window.location.origin + "/paiement",
    urlSuccess: window.location.origin + "/paiement",
    urlRefused: window.location.origin + "/paiement",
    urlCancel: window.location.origin + "/paiement",
    version: "V2",
    vadsCertificate: borne.zones[0].BnqKey
  };

  // AYB 2026.01.23 - Validation du format email : rejette les espaces et vérifie le format complet avec regex robuste
  const isValidateEmail = () => {
    if (!mailInfo || typeof mailInfo !== 'string') {
      setShowErrorMessage(true);
      return false;
    }
    const trimmedEmail = mailInfo.trim();
    if (trimmedEmail !== mailInfo) {
      setShowErrorMessage(true);
      return false;
    }
    if (/\s/.test(mailInfo)) {
      setShowErrorMessage(true);
      return false;
    }
    if (mailInfo.includes(' ')) {
      setShowErrorMessage(true);
      return false;
    }
    if (!emailRegex.test(mailInfo)) {
      setShowErrorMessage(true);
      return false;
    }
    if (mailInfo.length > 254) {
      setShowErrorMessage(true);
      return false;
    }
    if (/\.{2,}/.test(mailInfo) || mailInfo.startsWith('.') || mailInfo.startsWith('@') || mailInfo.endsWith('.')) {
      setShowErrorMessage(true);
      return false;
    }
    return true;
  };
  // #endregion

  // AYB 2026.02.16 - Polling déclenché quand l'iframe revient sur /paiement ou en fallback après 12s (3 tentatives, 2s d'intervalle)
  const validatingRef = useRef(false);
  const pendingAchatRef = useRef(null);
  const fallbackPollTimeoutRef = useRef(null);

  const clearPendingFromStorage = useCallback(() => {
    try {
      sessionStorage.removeItem(PENDING_ACHAT_KEY);
      sessionStorage.removeItem(PENDING_STAGE_KEY);
    } catch (_) {}
  }, []);

  const startPollingForResult = useCallback((idAchat, maxAttempts = POLL_MAX, delayMs = POLL_DELAY, showErrorOnExhausted = true) => {
    if (idAchat == null || idAchat === "") return;
    if (validatingRef.current) return;
    validatingRef.current = true;
    let attempt = 0;
    const fetchResult = () => {
      getResource(URL_BASE, tokenValue, RESSOURCE_PRE_ACHATS, idAchat)
        .then((data) => {
          const status = data?.transactionStatusPayzen;
          if (VALID_STATUS.includes(status)) {
            validatingRef.current = false;
            clearPendingFromStorage();
            onSuccessPaid(data);
            return;
          }
          if (status && REFUSED_STATUSES.includes(status)) {
            validatingRef.current = false;
            clearPendingFromStorage();
            setErrorPaymentStatus(status);
            setShowMessageErrorPaiement(true);
            onErrorPaid(data ?? null);
            return;
          }
          if (attempt < maxAttempts) {
            attempt += 1;
            setTimeout(fetchResult, delayMs);
          } else {
            validatingRef.current = false;
            if (showErrorOnExhausted) {
              clearPendingFromStorage();
              setErrorPaymentStatus(null);
              setShowMessageErrorPaiement(true);
              onErrorPaid(null);
            }
          }
        })
        .catch(() => {
          if (attempt < maxAttempts) {
            attempt += 1;
            setTimeout(fetchResult, delayMs);
          } else {
            validatingRef.current = false;
            if (showErrorOnExhausted) {
              clearPendingFromStorage();
              setErrorPaymentStatus(null);
              setShowMessageErrorPaiement(true);
              onErrorPaid(null);
            }
          }
        });
    };
    fetchResult();
  }, [tokenValue, onSuccessPaid, onErrorPaid, clearPendingFromStorage]);

  const onIframeLoad = useCallback(() => {
    const iframe = document.getElementById("urbaflux-payment-iframe");
    if (!iframe) return;
    try {
      const loc = iframe.contentWindow.location;
      if (!loc.pathname.startsWith("/paiement")) return;
      const qs = new URLSearchParams(loc.search || "");
      const isPayzenReturn = !!(
        qs.get("vads_trans_status") ||
        qs.get("vads_trans_uuid") ||
        qs.get("vads_result") ||
        qs.get("vads_auth_result")
      );
      if (!isPayzenReturn) return;
      if (validatingRef.current) return;
      const idAchat = qs.get("vads_order_id") || pendingAchatRef.current;
      if (!idAchat) return;
      pendingAchatRef.current = null;
      if (fallbackPollTimeoutRef.current) {
        clearTimeout(fallbackPollTimeoutRef.current);
        fallbackPollTimeoutRef.current = null;
      }
      try {
        sessionStorage.setItem(PENDING_STAGE_KEY, STAGE_RETURNED);
      } catch (_) {}
      startPollingForResult(idAchat);
    } catch {
    }
  }, [startPollingForResult]);

  useEffect(() => {
    const resume = () => {
      const pending = sessionStorage.getItem(PENDING_ACHAT_KEY);
      if (!pending) return;
      const stage = sessionStorage.getItem(PENDING_STAGE_KEY);
      if (stage !== STAGE_RETURNED) return;
      startPollingForResult(pending);
    };
    const onVis = () => {
      if (document.visibilityState === "visible") resume();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", resume);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", resume);
      if (fallbackPollTimeoutRef.current) {
        clearTimeout(fallbackPollTimeoutRef.current);
        fallbackPollTimeoutRef.current = null;
      }
    };
  }, [startPollingForResult]);
  //#endregion

  // #region HOOK D'EFFET
  const computeSignature = (orderIdValue) => {
    const p = { ...PARAMETRES, order_id: orderIdValue != null && orderIdValue !== "" ? String(orderIdValue) : "" };
    let s = "";
    Object.values(p).forEach(val => s = s.concat("+", val));
    return Base64.stringify(HmacSHA256(s.substring(1), p.vadsCertificate));
  };

  useEffect(() => {
    if (!montant || !isValidateEmail()) {
      return;
    }
    signature.current = computeSignature("");
    setSignatureReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borne, montant, dateMoment, mailInfo, i18n.language]);

  const submitFormWithPreAchat = () => {
    const form = document.getElementById("form-urbaflux");
    const doSubmit = () => {
      if (!form) return;
      form.submit();
    };
    const applyIdAchatAndSubmit = (idAchat) => {
      if (idAchat == null || !form) return;
      const orderInput = form.querySelector('input[name="vads_order_id"]');
      if (orderInput) orderInput.value = String(idAchat);
      const sigInput = form.querySelector('input[name="signature"]');
      if (sigInput) sigInput.value = computeSignature(idAchat);
      pendingAchatRef.current = idAchat;
      try {
        sessionStorage.setItem(PENDING_ACHAT_KEY, String(idAchat));
        sessionStorage.setItem(PENDING_STAGE_KEY, STAGE_SUBMITTED);
      } catch (_) {}
      if (fallbackPollTimeoutRef.current) clearTimeout(fallbackPollTimeoutRef.current);
      fallbackPollTimeoutRef.current = setTimeout(() => {
        fallbackPollTimeoutRef.current = null;
        if (!validatingRef.current && pendingAchatRef.current != null) {
          try {
            sessionStorage.setItem(PENDING_STAGE_KEY, STAGE_RETURNED);
          } catch (_) {}
          startPollingForResult(String(pendingAchatRef.current), FALLBACK_POLL_MAX, FALLBACK_POLL_INTERVAL_MS, false);
        }
      }, FALLBACK_POLL_DELAY_MS);
      doSubmit();
    };
    if (preAchatResult?.idAchat != null) {
      applyIdAchatAndSubmit(preAchatResult.idAchat);
      return;
    }
    Promise.resolve(onBeforeSubmit?.())
      .then((res) => {
        applyIdAchatAndSubmit(res?.idAchat);
      })
      .catch((err) => {
        if (err && typeof err.error === "string") {
          const msg = err.error.startsWith("TRANSLATION_KEY:")
            ? t(err.error.replace("TRANSLATION_KEY:", ""))
            : err.error;
          showError(msg);
        }
      });
  };

  useEffect(() => {
    if (!signatureReady || !userEmail || !isValidEmailFormat(userEmail) || mailInfo !== userEmail || hasAutoSubmitted.current) return;
    hasAutoSubmitted.current = true;
    submitFormWithPreAchat();
  }, [signatureReady, userEmail, mailInfo, onBeforeSubmit, preAchatResult]);
  // #endregion

  // #region INTERFACE
  return (
    !showMessageErrorPaiement
      ?
      <div className="container">
        <div>
          <iframe
            title="form"
            id="urbaflux-payment-iframe"
            name="formUrbaflux"
            src="about:blank"
            width={mailValidate ? 340 : 0}
            height={mailValidate ? 380 : 0}
            style={{ "visibility": mailValidate ? "visible" : "hidden" }}
            onLoad={onIframeLoad}
          />
          <form id="form-urbaflux" method="POST" target="formUrbaflux" action="https://secure.payzen.eu/vads-payment/" >
            <input type="hidden" name="vads_action_mode" value={PARAMETRES.action_mode} />
            <input type="hidden" name="vads_amount" value={montant.toString()} />
            <input type="hidden" name="vads_capture_delay" value={PARAMETRES.captureDelay} />
            <input type="hidden" name="vads_ctx_mode" value={PARAMETRES.ctxMode} />
            <input type="hidden" name="vads_currency" value={PARAMETRES.currency} />
            <input type="hidden" name="vads_cust_email" value={PARAMETRES.email} />
            <input type="hidden" name="vads_language" value={PARAMETRES.language} />
            <input type="hidden" name="vads_order_info" value={PARAMETRES.info} />
            <input type="hidden" name="vads_order_id" value={PARAMETRES.order_id} />
            <input type="hidden" name="vads_page_action" value={PARAMETRES.pageAction} />
            <input type="hidden" name="vads_payment_cards" value={PARAMETRES.paymentCard} />
            <input type="hidden" name="vads_payment_config" value={PARAMETRES.paymentConfig} />
            <input type="hidden" name="vads_redirect_error_timeout" value={PARAMETRES.redirectErrorTimeout} />
            <input type="hidden" name="vads_redirect_success_timeout" value={PARAMETRES.redirectSuccessTimeout} />
            <input type="hidden" name="vads_return_mode" value={PARAMETRES.returnMode} />
            <input type="hidden" name="vads_site_id" value={PARAMETRES.siteId} />
            <input type="hidden" name="vads_theme_config" value={PARAMETRES.themeConfig} />
            <input type="hidden" name="vads_trans_date" value={PARAMETRES.transDate} />
            <input type="hidden" name="vads_trans_id" value={PARAMETRES.transId} />
            <input type="hidden" name="vads_url_return" value={PARAMETRES.urlReturn} />
            <input type="hidden" name="vads_url_success" value={PARAMETRES.urlSuccess} />
            <input type="hidden" name="vads_url_refused" value={PARAMETRES.urlRefused} />
            <input type="hidden" name="vads_url_cancel" value={PARAMETRES.urlCancel} />
            <input type="hidden" name="vads_version" value={PARAMETRES.version} />
            <input type="hidden" name="signature" value={signature.current} />
          </form>
        </div>

        <div className="container" style={{ visibility: !mailValidate ? "visible" : "hidden" }}>
          <div className="textContainer">
            <p className="textTitle" >{t("payment.mailToTicket")}</p>
            <TextField
              label={t("achat.email")}
              name="email"
              onChange={(e) => [setMailInfo(e.target.value), setShowErrorMessage(false)]}
              outlined
              required
              type="email"
              value={mailInfo}
            />
            {showErrorMessage
              ?
              <p className="textError">{t("payment.mailToValidation")}</p>
              :
              null
            }
          </div>
          <Fab
            id="urbaflux-fab-validate"
            type="button"
            label={`${t("achat.validation")}`}
            onClick={() => isValidateEmail() ? setModaleConfirmMail(true) : null}
          />
        </div>
        <ConfirmModal
          visible={modaleConfirmMail}
          onClose={() => setModaleConfirmMail(false)}
          onValid={() => {
            setModaleConfirmMail(false);
            setMailValidate(true);
            submitFormWithPreAchat();
          }}
          title={t("achat.confirmTitleMail")}
          children={t("achat.confirmMessageMail")}
          info={mailInfo}
          height={"250px"}
        />
      </div>
      :
      <div className="containerError">
        <p className="textTitle">
          {errorPaymentStatus ? (t(`achat.error${errorPaymentStatus.charAt(0) + errorPaymentStatus.slice(1).toLowerCase()}`) || t("achat.error")) : t("achat.error")}
        </p>
        <Fab
          label="Nouvel essai"
          onClick={() => window.location.reload()}
        />
      </div>
  );
  // #endregion
};
// #endregion

// #region PROPRIETES
/**
 * Type des propriétés de {@link PaymentForm}
 * @typedef {Object} PaymentForm.propTypes
 * @property {object} borne Terminal selectionné
 * @property {number} [delaiPayzen] Délai pour le transfert Payzen
 * @property {string} [devise=null] Devise au format ISO 4217
 * @property {number} montant Montant de la transaction
 * @property {function} onBeforeSubmit Fonction appelée avant envoi du formulaire Payzen (peut retourner une Promise)
 * @property {function} onErrorPaid Fonction callback sur erreur du paiement
 * @property {object} [preAchatResult] Résultat du pre-achat (201) ; si fourni, le formulaire utilise idAchat sans rappeler onBeforeSubmit
 * @property {function} onSuccessPaid Fonction callback au succès du paiement
*/
PaymentForm.propTypes = {
  borne: PropTypes.object.isRequired,
  delaiPayzen: PropTypes.number.isRequired,
  devise: PropTypes.string,
  montant: PropTypes.number.isRequired,
  onBeforeSubmit: PropTypes.func,
  onErrorPaid: PropTypes.func.isRequired,
  preAchatResult: PropTypes.object,
  onSuccessPaid: PropTypes.func.isRequired
};

// Valeurs par défault des propriétés
PaymentForm.defaultProps = {
  devise: null
};
// #endregion

export { PaymentForm };

